import { Code, ConnectError } from '@connectrpc/connect';
import { authClient } from './api-grpc';
import authStore from '../stores/auth.store';
import {
  CheckTokenResponse,
  CheckTokenResponseSchema,
  CompleteTemporaryPasswordResponse,
  LoginResponse,
  RefreshTokenRequestSchema,
  RegisterResponse,
} from '../gen/proto/api/gateway/v1/auth_pb';
import { create } from '@bufbuild/protobuf';

export class TemporaryPasswordError extends Error {
  constructor(public readonly email: string) {
    super('temporary_password_required');
    this.name = 'TemporaryPasswordError';
  }
}

export const authApi = {
  login: async function (params: { username: string; password: string }): Promise<LoginResponse> {
    try {
      const response = await authClient.login({
        email: params.username,
        password: params.password,
      });

      authStore.setState({
        authToken: response.accessToken,
        refreshToken: response.refreshToken,
      });

      return response;
    } catch (error) {
      if (error instanceof ConnectError && error.code === Code.FailedPrecondition) {
        throw new TemporaryPasswordError(params.username);
      }
      throw error;
    }
  },

  completeTemporaryPassword: async function (params: {
    email: string;
    temporaryPassword: string;
    newPassword: string;
  }): Promise<CompleteTemporaryPasswordResponse> {
    const response = await authClient.completeTemporaryPassword({
      email: params.email,
      temporaryPassword: params.temporaryPassword,
      newPassword: params.newPassword,
    });

    authStore.setState({
      authToken: response.accessToken,
      refreshToken: response.refreshToken,
    });

    return response;
  },

  logout: async function (refreshToken: string): Promise<boolean> {
    try {
      await authClient.logout({
        refreshToken: refreshToken,
      });

      // Clear tokens from authStore
      authStore.setState({
        authToken: undefined,
        refreshToken: undefined,
      });

      return true;
    } catch (error) {
      throw error;
    }
  },

  register: async function (params: { username: string; password: string; email: string }): Promise<RegisterResponse> {
    try {
      const response = await authClient.register({
        password: params.password,
        email: params.email,
      });

      // Store tokens and user info in authStore
      authStore.setState({
        authToken: response.accessToken,
        refreshToken: response.refreshToken,
      });

      return response;
    } catch (error) {
      throw error;
    }
  },

  checkToken: async function (token: string): Promise<CheckTokenResponse> {
    try {
      const response = await authClient.checkToken({
        token: token,
      });

      // Update user info in authStore if token is valid
      if (response.tokenValid) {
        authStore.setState({
          email: response.email,
        });
      }

      return response;
    } catch (error) {
      return create(CheckTokenResponseSchema, {
        tokenValid: false,
        email: '',
      });
    }
  },

  resetPassword: async function (email: string): Promise<{ success: boolean; message: string }> {
    return {
      success: true,
      message: 'Password reset email sent successfully (mock)',
    };
  },

  updateUser: async function (userData: { email?: string; username?: string }): Promise<{
    success: boolean;
    message: string;
  }> {
    return {
      success: true,
      message: 'User updated successfully (mock)',
    };
  },

  refreshToken: async function (): Promise<string | undefined> {
    try {
      const { refreshToken } = authStore.getState();
      if (!refreshToken) return undefined;

      const request = create(RefreshTokenRequestSchema, {
        refreshToken: refreshToken,
      });
      const response = await authClient.refreshToken(request);

      if (response.accessToken) {
        authStore.setState({
          authToken: response.accessToken,
          refreshToken: response.refreshToken,
        });
        return response.accessToken;
      }

      // Clear tokens if token is invalid
      authStore.setState({
        authToken: undefined,
        refreshToken: undefined,
      });

      return undefined;
    } catch (error) {
      // Clear tokens on error
      authStore.setState({
        authToken: undefined,
        refreshToken: undefined,
      });
      return undefined;
    }
  },
};
